import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import JobCard from "@/components/JobCard";
import { useBrand } from "@/contexts/BrandContext";
import { ArrowRight, Sparkles, Shield, LineChart } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1758448721134-1798533ae917?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBuaWdodHxlbnwwfHx8fDE3Nzc4NDkxNDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
    const brand = useBrand();
    const { search } = useLocation();
    const [jobs, setJobs] = useState([]);

    useEffect(() => {
        api.get("/jobs").then((r) => setJobs(r.data.slice(0, 6))).catch(() => setJobs([]));
    }, []);

    return (
        <div data-testid="landing-page">
            {/* Hero */}
            <section
                data-testid="hero-section"
                className="relative grain overflow-hidden"
            >
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url(${HERO_IMG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        opacity: 0.35,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/30 via-[#0A0A0A]/70 to-[#0A0A0A]" />
                <div className="relative mx-auto flex min-h-[88vh] max-w-[1400px] flex-col justify-center px-6 py-24 md:px-10">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="caption mb-6 inline-flex items-center gap-2" style={{ color: "var(--brand-accent)" }}>
                            <span className="h-px w-8" style={{ background: "var(--brand-accent)" }} />
                            {brand.isWhiteLabel ? `${brand.name} • Powered by Sovereign` : "Sovereign Recruitment Hub"}
                        </div>
                        <h1 className="font-serif text-6xl tracking-display sm:text-7xl lg:text-8xl text-[#FAFAFA] leading-[0.95]">
                            The world's most <em className="italic" style={{ color: "var(--brand-accent)" }}>discerning</em> talent.
                            <br />Placed with intent.
                        </h1>
                        <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-[#A3A3A3]">
                            A curated marketplace for executive and senior-level mandates.
                            Submit a candidate, receive an AI Match Overview in seconds, and
                            collect placement bounties of <span className="text-[#FAFAFA]">$10,000–$25,000</span>.
                        </p>
                        <div className="mt-12 flex flex-wrap gap-4">
                            <Link
                                to={{ pathname: "/jobs", search }}
                                data-testid="cta-view-jobs"
                                className="inline-flex items-center justify-center gap-2 rounded-sm px-8 py-4 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.4)]"
                                style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                            >
                                Browse Mandates <ArrowRight size={16} />
                            </Link>
                            <Link
                                to={{ pathname: "/register", search }}
                                data-testid="cta-become-recruiter"
                                className="inline-flex items-center justify-center rounded-sm border border-white/10 px-8 py-4 text-sm font-medium text-[#FAFAFA] transition-all duration-300 hover:border-white/30 hover:bg-white/5"
                            >
                                Become a Recruiter
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pillars */}
            <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-10">
                <div className="mb-12 max-w-2xl">
                    <div className="caption text-[var(--brand-accent)]">The Sovereign Standard</div>
                    <h2 className="mt-4 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                        Quiet excellence. Verified outcomes.
                    </h2>
                </div>
                <div className="grid gap-px bg-white/5 sm:grid-cols-3">
                    {[
                        { Icon: Sparkles, title: "Match Overview Engine", body: "AI-powered scorecards grade every candidate against the mandate — skills, experience, comp, and location — in under ten seconds." },
                        { Icon: LineChart, title: "Placement Bounties", body: "Earn $10k–$25k per successful placement. Transparent pipelines, no clawbacks, no nonsense." },
                        { Icon: Shield, title: "White-Label Ready", body: "Operate under your own brand. Append a query string and the entire experience reskins to your firm in seconds." },
                    ].map(({ Icon, title, body }) => (
                        <div key={title} className="bg-[#0A0A0A] p-10">
                            <Icon size={28} style={{ color: "var(--brand-accent)" }} />
                            <h3 className="mt-6 font-serif text-2xl tracking-display text-[#FAFAFA]">{title}</h3>
                            <p className="mt-3 text-[#A3A3A3] leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured jobs */}
            <section className="mx-auto max-w-[1400px] px-6 pb-24 md:px-10">
                <div className="mb-10 flex items-end justify-between">
                    <div>
                        <div className="caption text-[var(--brand-accent)]">Selected Mandates</div>
                        <h2 className="mt-4 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                            Live opportunities
                        </h2>
                    </div>
                    <Link
                        to={{ pathname: "/jobs", search }}
                        data-testid="cta-all-jobs"
                        className="caption hidden text-[#A3A3A3] transition-colors hover:text-[#FAFAFA] sm:inline-flex"
                    >
                        View all →
                    </Link>
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {jobs.map((j) => <JobCard key={j.id} job={j} />)}
                </div>
            </section>
        </div>
    );
}
